/**
 * Country sphere coordinate conversion
 *
 * Ported from Python (map_projection/svg_model.py lines 298-336)
 * Converts 2D SVG coordinates to 3D sphere coordinates for projection.
 */

import { mercatorInverse } from './projections.js';

/**
 * Normalize SVG pixel coordinates to [0, 1]
 *
 * @param {Float32Array} coords - [x0,y0, x1,y1, ...] in SVG pixels
 * @param {number} width - SVG viewport width
 * @param {number} height - SVG viewport height
 * @returns {Float32Array} Normalized coordinates in [0,1]
 */
function normalizeCoordinates(coords, width, height) {
    const normalized = new Float32Array(coords.length);
    for (let i = 0; i < coords.length; i += 2) {
        normalized[i] = coords[i] / width;       // x to [0,1]
        normalized[i + 1] = coords[i + 1] / height; // y to [0,1]
    }
    return normalized;
}

/**
 * Convert normalized 2D coordinates to 3D sphere coordinates
 * Uses inverse Mercator projection to get lat/lon, then converts to Cartesian
 *
 * @param {Float32Array} coords2D - [x0,y0, x1,y1, ...] in [0,1]
 * @returns {Float32Array} [x0,y0,z0, x1,y1,z1, ...] on unit sphere
 */
function convertToSphere(coords2D) {
    const numVertices = coords2D.length / 2;
    const coords3D = new Float32Array(numVertices * 3);

    for (let i = 0; i < numVertices; i++) {
        const x = coords2D[i * 2];
        const y = coords2D[i * 2 + 1];

        // Apply inverse Mercator: [0,1] → [lat, lon] in radians
        const [lat, lon] = mercatorInverse(x, y);

        // Convert spherical → Cartesian on unit sphere
        // x = cos(lat) * cos(lon)
        // y = cos(lat) * sin(lon)
        // z = sin(lat)
        const sphereX = Math.cos(lat) * Math.cos(lon);
        const sphereY = Math.cos(lat) * Math.sin(lon);
        const sphereZ = Math.sin(lat);

        coords3D[i * 3] = sphereX;
        coords3D[i * 3 + 1] = sphereY;
        coords3D[i * 3 + 2] = sphereZ;
    }

    return coords3D;
}

/**
 * Convert parsed SVG data to include sphere coordinates
 * Adds normalized 2D and sphere 3D coordinates to all country paths
 *
 * @param {Object} svgData - Data from svg-parser.js
 * @returns {Object} Enhanced with normalized 2D and sphere 3D coords
 */
export function buildCountrySphere(svgData) {
    const { countries, width, height } = svgData;

    console.log('Converting coordinates to sphere...');

    let totalVertices = 0;

    for (const [id, country] of countries) {
        for (const path of country.paths) {
            // Add normalized 2D coordinates [0,1]
            path.normalized2D = normalizeCoordinates(path.svg2D, width, height);

            // Convert to 3D sphere coordinates
            path.sphere3D = convertToSphere(path.normalized2D);

            totalVertices += path.vertexCount;
        }
    }

    console.log(`Converted ${totalVertices} vertices to sphere coordinates`);

    return svgData;
}

/**
 * All-in-one function: load SVG and build sphere data
 * Convenience wrapper for the full pipeline
 *
 * @param {string} svgPath - Path to SVG file (e.g., '/assets/world.svg')
 * @returns {Promise<Object>} Complete country data with all coordinate systems
 */
export async function loadCountryData(svgPath) {
    const { loadSVG } = await import('./svg-parser.js');

    const startTime = performance.now();

    // Load and parse SVG
    const svgData = await loadSVG(svgPath);

    // Convert to sphere coordinates
    const countryData = buildCountrySphere(svgData);

    const loadTime = performance.now() - startTime;
    console.log(`Total load time: ${loadTime.toFixed(2)}ms`);

    // Calculate memory usage
    let totalMemory = 0;
    for (const [id, country] of countryData.countries) {
        for (const path of country.paths) {
            totalMemory += path.svg2D.byteLength;
            totalMemory += path.normalized2D.byteLength;
            totalMemory += path.sphere3D.byteLength;
        }
    }
    console.log(`Memory usage: ${(totalMemory / 1024 / 1024).toFixed(2)} MB`);

    return countryData;
}

/**
 * Validate country data structure and coordinate ranges
 *
 * @param {Object} data - Country data to validate
 * @returns {Array<string>} Array of validation issues (empty if all valid)
 */
export function validateCountryData(data) {
    const issues = [];

    for (const [id, country] of data.countries) {
        // Check required fields
        if (!country.id || !country.name || !country.color) {
            issues.push(`${id}: Missing required fields`);
        }

        // Check color range [0-255]
        if (country.color.some(c => c < 0 || c > 255)) {
            issues.push(`${id}: Invalid color range`);
        }

        for (const [pathIdx, path] of country.paths.entries()) {
            // Check normalized coords in [0,1]
            for (let i = 0; i < path.normalized2D.length; i++) {
                const val = path.normalized2D[i];
                if (val < 0 || val > 1) {
                    issues.push(`${id} path ${pathIdx}: Normalized coord out of range: ${val}`);
                    break; // Only report once per path
                }
            }

            // Check sphere coords on unit sphere (x²+y²+z² ≈ 1)
            for (let i = 0; i < path.sphere3D.length; i += 3) {
                const x = path.sphere3D[i];
                const y = path.sphere3D[i + 1];
                const z = path.sphere3D[i + 2];
                const r = Math.sqrt(x * x + y * y + z * z);
                if (Math.abs(r - 1.0) > 0.01) {
                    issues.push(`${id} path ${pathIdx}: Sphere coord not on unit sphere: radius=${r.toFixed(4)}`);
                    break; // Only report once per path
                }
            }

            // Check for NaN/Infinity
            const allCoords = [...path.svg2D, ...path.normalized2D, ...path.sphere3D];
            if (allCoords.some(c => !isFinite(c))) {
                issues.push(`${id} path ${pathIdx}: Non-finite coordinate`);
            }
        }
    }

    return issues;
}
